import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Search, 
  Building, 
  Contact, 
  FileText, 
  Compass, 
  Database, 
  CheckCircle, 
  Target, 
  Mic, 
  Shield, 
  Lock, 
  Rocket, 
  TrendingUp,
  Users,
  Star,
  Award,
  ArrowRight,
  X,
  Zap,
  Eye,
  Calendar,
  Phone,
  Mail,
  BarChart3,
  Timer,
  UserCheck,
  Brain,
  Network,
  Quote,
  Percent,
  Clock,
  Briefcase,
  MapPin,
  Lightbulb,
  Headphones
} from 'lucide-react';

const JobIntelSalesPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-32 px-4 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-repeat" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M50 50c13.8 0 25-11.2 25-25S63.8 0 50 0 25 11.2 25 25s11.2 25 25 25zm0 25c13.8 0 25-11.2 25-25S63.8 50 50 50s-25 11.2-25 25 11.2 25 25 25zM25 25c13.8 0 25-11.2 25-25S38.8-25 25-25 0-13.8 0 0s11.2 25 25 25zm50 0c13.8 0 25-11.2 25-25S88.8-25 75-25 50-13.8 50 0s11.2 25 25 25zm0 50c13.8 0 25-11.2 25-25S88.8 25 75 25 50 36.2 50 50s11.2 25 25 25zm-50 0c13.8 0 25-11.2 25-25S38.8 25 25 25 0 36.2 0 50s11.2 25 25 25z'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-400 rounded-full opacity-20 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-indigo-400 rounded-full opacity-20 animate-pulse delay-1000" />
        <div className="absolute top-1/3 right-20 w-12 h-12 bg-cyan-400 rounded-full opacity-20 animate-pulse delay-500" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="mb-8">
            <Badge className="bg-yellow-400 text-black hover:bg-yellow-300 text-lg px-6 py-2 font-bold">
              ⚡ The JobIntel Suite™
            </Badge>
          </div>
          
          <h1 className="text-4xl md:text-7xl font-black text-white mb-8 leading-tight tracking-tight">
            JobIntel Suite™ Delivers an 
            <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent"> 85% Interview Acquisition Rate</span> — Converting 1 in 3 Interviews into Job Offers
          </h1>
          
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-5xl mx-auto leading-relaxed font-light">
            With the <span className="font-semibold text-white">Pay, Perks, and Work-Life Balance of Your Dreams.</span>
          </p>
          
          <p className="text-lg md:text-xl text-slate-200 mb-12 max-w-4xl mx-auto leading-relaxed">
            A complete job search intelligence system that helps you uncover the right opportunities, connect directly with decision makers, instantly prove you're the solution to their biggest challenges, consistently stand out as the front-running favorite in every interview, and secure offers at an astonishingly high rate.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black hover:from-yellow-300 hover:to-orange-300 font-bold px-12 py-6 text-xl rounded-full shadow-2xl transform hover:scale-105 transition-all duration-200">
              Get JobIntel Suite Today
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-12 py-6 text-xl rounded-full font-semibold">
              See Plans & Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              😖 The Problem
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-2xl font-semibold text-gray-800 mb-8 leading-relaxed">
                Most job seekers are stuck in a loop they can't explain.
              </p>
              
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                They apply online, day after day, tailoring their résumés the best they know how, checking every box, and still… nothing. No interview. No call. No feedback. Just rejection emails or, even worse, complete silence.
              </p>
              
              <p className="text-lg text-gray-700 mb-12 leading-relaxed">
                If that sounds familiar, you're not alone. The truth is:
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6 p-6 bg-white rounded-2xl shadow-lg border-l-4 border-red-500">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <X className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-red-600 mb-2">Over 96%</p>
                    <p className="text-gray-800 font-semibold">of online applications are never seen by a human being.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6 p-6 bg-white rounded-2xl shadow-lg border-l-4 border-red-500">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-red-600 mb-2">500-700+</p>
                    <p className="text-gray-800 font-semibold">The average job posting on LinkedIn or Indeed attracts applicants in the first week.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6 p-6 bg-white rounded-2xl shadow-lg border-l-4 border-red-500">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-red-600 mb-2">ATS Systems</p>
                    <p className="text-gray-800 font-semibold">filter out the majority of candidates in seconds.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6 p-6 bg-white rounded-2xl shadow-lg border-l-4 border-red-500">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Percent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-red-600 mb-2">Only 2%</p>
                    <p className="text-gray-800 font-semibold">of online applications ever lead to interviews.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6 p-6 bg-white rounded-2xl shadow-lg border-l-4 border-red-500">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-red-600 mb-2">85%</p>
                    <p className="text-gray-800 font-semibold">of jobs are never posted publicly. Only 8-10% of job seekers know how to access this hidden market.</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-gray-900 rounded-2xl">
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  And here's the hidden trap: <span className="text-yellow-400 font-bold">90% of job seekers compete for the 15% of openings that are publicly listed</span> on LinkedIn, Indeed, ZipRecruiter, and Monster. Meanwhile, 85% of jobs are never posted publicly. Only 8–10% of job seekers know how to access this hidden market — and that's why most stay stuck.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-8 rounded-3xl shadow-2xl">
                <div className="space-y-4">
                  <div className="bg-red-100 p-6 rounded-xl border border-red-200 animate-pulse">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <p className="text-red-800 font-bold">Application Rejected</p>
                    </div>
                    <p className="text-sm text-gray-600">Your application was not selected to move forward...</p>
                  </div>
                  <div className="bg-orange-100 p-6 rounded-xl border border-orange-200 animate-pulse delay-500">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <p className="text-orange-800 font-bold">No Response - 47 Days</p>
                    </div>
                    <p className="text-sm text-gray-600">Applied 47 days ago - Complete silence...</p>
                  </div>
                  <div className="bg-red-100 p-6 rounded-xl border border-red-200 animate-pulse delay-1000">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <p className="text-red-800 font-bold">Position Filled</p>
                    </div>
                    <p className="text-sm text-gray-600">We have decided to move forward with another candidate...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-3xl shadow-xl max-w-4xl mx-auto">
              <Quote className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-xl md:text-2xl font-medium italic leading-relaxed">
                "But take heart. Closed doors do not mean you've been forgotten. Sometimes the waiting season is preparation. Sometimes rejection is redirection. This is the game you've unknowingly been playing — and JobIntel changes the rules."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              🌟 The Solution (Enhanced)
            </h2>
            <p className="text-2xl font-semibold text-gray-800 mb-4">
              The JobIntel Suite™ flips the script on job searching.
            </p>
            <p className="text-xl text-gray-700 max-w-5xl mx-auto leading-relaxed">
              Instead of wasting months on ghost jobs and rejection emails, you'll finally have the system, data, and automation that put you in control.
            </p>
          </div>
          
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Here's what that means in real numbers:
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Zap className="w-10 h-10" />,
                  title: "Verify jobs instantly",
                  description: "Nearly 47% of online postings are fake or expired. JobIntel identifies them on the spot and tells you whether a role is still hiring with a percentage score — saving you from chasing dead ends.",
                  stat: "47% avoided",
                  color: "from-blue-500 to-cyan-500"
                },
                {
                  icon: <Target className="w-10 h-10" />,
                  title: "Beat the ATS every time",
                  description: "Over 96% of résumés never reach a human. With the TailorWiz Formula™ and ATS Blueprint™, you'll pass the bots 100% of the time and land directly in front of real decision makers.",
                  stat: "100% pass rate",
                  color: "from-green-500 to-emerald-500"
                },
                {
                  icon: <Users className="w-10 h-10" />,
                  title: "Compete smarter",
                  description: "The average job posting draws 500-700+ applicants. JobIntel uncovers hidden opportunities where you're competing with 20 people or fewer — often leading to interviews within days instead of months.",
                  stat: "20 vs 500+",
                  color: "from-purple-500 to-violet-500"
                },
                {
                  icon: <Calendar className="w-10 h-10" />,
                  title: "Land interviews predictably",
                  description: "Instead of the broken 2% industry average, JobIntel users achieve an 85% interview acquisition rate — with 2–5+ interviews per week (USUALLY MORE!) starting within 7-14 days.",
                  stat: "85% success",
                  color: "from-orange-500 to-red-500"
                },
                {
                  icon: <Award className="w-10 h-10" />,
                  title: "Turn interviews into offers",
                  description: "On average, 1 in 3 interviews results in a job offer — with the pay, benefits, and job settings you actually want.",
                  stat: "1 in 3 offers",
                  color: "from-yellow-500 to-orange-500"
                },
                {
                  icon: <CheckCircle className="w-10 h-10" />,
                  title: "Dominate the interview room",
                  description: "InterviewMagic™ simulates the exact questions you'll face, scores your answers, and coaches you with STAR feedback — so you walk in as the front-running favorite.",
                  stat: "Front-runner",
                  color: "from-indigo-500 to-blue-500"
                }
              ].map((item, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-green-400 transition-all duration-300 hover:shadow-2xl group overflow-hidden">
                  <CardHeader className="text-center relative">
                    <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      {item.icon}
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-green-600 transition-colors">
                      {item.title}
                    </CardTitle>
                    <Badge className="bg-green-100 text-green-800 border-green-200 font-bold text-lg px-4 py-1">
                      {item.stat}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          <div className="text-center mb-12">
            <p className="text-xl text-gray-800 mb-8 font-semibold">
              This isn't theory. It's data, psychology, and automation working together to remove every barrier that keeps job seekers stuck:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                { text: "Ghost jobs", status: "eliminated" },
                { text: "ATS rejection", status: "eliminated" },
                { text: "Overcrowded job boards", status: "bypassed" },
                { text: "Generic résumés", status: "replaced with precision-tailored assets" },
                { text: "HR black holes", status: "bypassed with direct hiring manager outreach" },
                { text: "Interview anxiety", status: "crushed with AI-powered simulations" }
              ].map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg border-2 border-green-200">
                  <p className="text-gray-800 font-semibold mb-2">{item.text}</p>
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-600 font-bold">{item.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900 mb-8">
              With JobIntel, you stop hoping for results — and start producing them consistently.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold px-12 py-6 text-xl rounded-full shadow-xl">
              See How JobIntel Works
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>

      {/* Reports Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              📊 Reports: Intelligence That Wins Interviews
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Search className="w-10 h-10" />,
                title: "Job Legitimacy Report™",
                pain: "Nearly 47% of job listings on major platforms are fake, expired, or ghost jobs. Many are duplicated across multiple boards, so a single role can generate 2,000+ applications in days — even when it's already filled. The result? Wasted time, false hope, and emotional burnout. Every minute spent on a fake job is a minute lost on the real opportunities that could change your life.",
                solution: "The Job Legitimacy Report™ instantly verifies if a job posting is real, expired, duplicated, or fake. It doesn't stop there — it also shows hiring trends for the last 7, 30, and 90 days, then gives you a percentage-based 'Still Hiring' score. Instead of guessing, you'll know with certainty if the employer is still actively hiring. This saves you hours, protects you from scams, and ensures you only chase roles worth pursuing.",
                color: "blue"
              },
              {
                icon: <Building className="w-10 h-10" />,
                title: "Advanced Company Insight Report™",
                pain: "Walking into an interview blind is career suicide. Most candidates only know what they read on the job posting or Google — but that's surface-level. Without understanding a company's top pain points and goals, you can't position yourself as the solution. That's why even highly qualified applicants leave interviews empty-handed.",
                solution: "The Advanced Company Insight Report™ reveals the department's 3 biggest pain points and 3 biggest goals, giving you insider knowledge to tailor your messaging. Plus, it includes the Need-to-Know Company Info Report™, which answers: What are the 5 most important things you should know about this company right now? What's their history and culture? What should you wear to the interview? Instead of guessing or rambling, you'll show up with precise talking points that make hiring managers say: 'This person gets it.'",
                color: "indigo"
              },
              {
                icon: <Contact className="w-10 h-10" />,
                title: "Decision Maker's Contact Report™",
                pain: "Over 96% of résumés never reach a human being. They get filtered by ATS or lost in a recruiter's black hole inbox. Most job seekers spend weeks applying only to be ignored — not because they weren't qualified, but because they never reached the decision maker.",
                solution: "The Decision Maker's Contact Report™ puts you directly in front of the people who matter — hiring managers, recruiters, and even backup contacts. It also gives you the exact outreach roadmap (who to message, when to message, and what to say). No more waiting on HR gatekeepers. No more hoping. You'll be speaking directly to the person who can say: 'You're hired.'",
                color: "green"
              },
              {
                icon: <FileText className="w-10 h-10" />,
                title: "Solution Asset Report™",
                pain: "Most candidates show up to interviews with generic résumés and rehearsed answers. Hiring managers aren't impressed — they're looking for proof you can solve their specific problems. Without that proof, you're just another résumé in the pile.",
                solution: "The Solution Asset Report™ creates a tailored document that shows exactly how you solve the company's top challenges. It's more than a résumé — it's a weapon that positions you as the answer they've been searching for. Use it in your cover letter, email outreach, or interview — and watch your credibility skyrocket instantly.",
                color: "orange"
              },
              {
                icon: <Compass className="w-10 h-10" />,
                title: "Career Clarity Match Report™",
                pain: "Too many job seekers chase roles they don't actually want — or worse, land a job that feels like a bad fit within weeks. Without clarity, you risk bouncing from job to job, burning out, and never building momentum in your career.",
                solution: "The Career Clarity Match Report™ aligns your personal career goals with the right opportunities. It analyzes whether the company, culture, and role match your long-term trajectory. Instead of chasing 'any job,' you'll finally pursue the right job — one that aligns with your future and doesn't leave you stuck in another cycle of regret.",
                color: "purple"
              }
            ].map((report, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-2xl group h-full">
                <CardHeader>
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 text-white shadow-lg
                    ${report.color === 'blue' ? 'bg-gradient-to-br from-blue-500 to-cyan-500' : ''}
                    ${report.color === 'indigo' ? 'bg-gradient-to-br from-indigo-500 to-purple-500' : ''}
                    ${report.color === 'green' ? 'bg-gradient-to-br from-green-500 to-emerald-500' : ''}
                    ${report.color === 'orange' ? 'bg-gradient-to-br from-orange-500 to-red-500' : ''}
                    ${report.color === 'purple' ? 'bg-gradient-to-br from-purple-500 to-pink-500' : ''}
                  `}>
                    {report.icon}
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {report.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <p className="text-sm font-bold text-red-700 mb-2">The Pain:</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{report.pain}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <p className="text-sm font-bold text-green-700 mb-2">The Solution:</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{report.solution}</p>
                  </div>
                  <Button variant="link" className="p-0 h-auto font-bold text-blue-600 hover:text-blue-800">
                    Learn More →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold px-12 py-6 text-xl rounded-full shadow-xl">
              Unlock All JobIntel Reports
            </Button>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              ⚙️ Tools: Automation & Execution
            </h2>
          </div>
          
          <div className="space-y-20">
            {[
              {
                icon: <Database className="w-16 h-16" />,
                title: "Advanced Job Search & Tracking Hub™",
                pain: "The average online job posting attracts 500–700+ applicants in the first week. ATS bots reject most within seconds, leaving you in the 2% who might make it through. Worse, 85% of jobs are never even posted online. That means you're competing with thousands of people for just a fraction of the opportunities that exist.",
                solution: "The Advanced Job Search & Tracking Hub™ doesn't just scrape LinkedIn or Indeed — it monitors 300+ job boards, 150+ niche sites, and the top 100 company career pages, plus any custom list you add. The second a matching job is posted, you get a real-time alert by phone and email. You'll be among the first to apply — while everyone else is still refreshing job boards. It also tracks every opportunity in one place with filters like applied/not applied, filled/not filled, watchlist, and favorites. With JobIntel, you'll never miss the right job again.",
                features: ["Real-time phone and email alerts", "Monitors 300+ job boards and 150+ niche sites", "Custom company tracking", "Smart filters: applied/not applied, filled/not filled", "Never miss the right job again"],
                side: "left"
              },
              {
                icon: <CheckCircle className="w-16 h-16" />,
                title: "JobIntel Workflow™ – The Science of Getting Job Interviews",
                pain: "Most job seekers apply blindly, hoping the résumé they uploaded was 'good enough.' Hope is not a strategy. Without a system, they lose track of applications, miss key steps, and never follow through effectively.",
                solution: "The JobIntel Workflow™ gives you a repeatable 8-step system that turns applications into interviews. Every saved job generates its own interactive checklist — from confirming the employer is hiring, to tailoring your résumé the right way, to delivering it directly to the hiring manager. You'll never wonder what to do next. You'll know exactly how to move each application toward an interview.",
                features: ["Proven 8-step interactive checklist", "Moves every application from submission to interview", "Track progress with precision", "Never lose applications or miss steps", "Eliminates guesswork completely"],
                side: "right"
              },
              {
                icon: <Mic className="w-16 h-16" />,
                title: "JobIntel InterviewMagic™",
                pain: "Even when you land the interview, nerves, lack of prep, and generic answers kill your chances. Most candidates recycle the same clichés, stumble through STAR responses, and leave hiring managers unconvinced.",
                solution: "JobIntel InterviewMagic™ is like a flight simulator for interviews. Upload the job description or URL and it generates the most likely questions for that exact role. You can run 1-on-1 or panel simulations, while AI analyzes your tone, filler words, body language, and energy. Then it gives you a score + detailed feedback, along with STAR framework coaching. By the time you step into the real interview, you'll already know what they'll ask — and how to answer like the #1 candidate.",
                features: ["1-on-1 and panel interview simulations", "AI analyzes tone, filler words, body language", "Generates questions for your exact role", "STAR framework coaching", "Detailed scoring and feedback", "Walk in as the front-running favorite"],
                side: "left"
              }
            ].map((tool, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-indigo-400 transition-all duration-300 hover:shadow-2xl overflow-hidden">
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${tool.side === 'right' ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className="p-12">
                    <div className="flex items-center gap-6 mb-8">
                      <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center text-white shadow-xl">
                        {tool.icon}
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-gray-900">{tool.title}</h3>
                      </div>
                    </div>
                    
                    <div className="space-y-6 mb-8">
                      <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                        <p className="text-sm font-bold text-red-700 mb-3">The Pain:</p>
                        <p className="text-gray-700 leading-relaxed">{tool.pain}</p>
                      </div>
                      <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                        <p className="text-sm font-bold text-green-700 mb-3">The Solution:</p>
                        <p className="text-gray-700 leading-relaxed">{tool.solution}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {tool.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold px-8 py-3 rounded-full">
                      Learn More →
                    </Button>
                  </div>
                  
                  <div className={`relative ${tool.side === 'right' ? 'lg:order-first' : ''}`}>
                    <div className="bg-gradient-to-br from-gray-900 to-indigo-900 p-8 rounded-3xl shadow-2xl">
                      <div className="bg-gray-800 rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <div className="space-y-4 text-green-400 font-mono text-sm">
                          <div>$ jobintel --scan --realtime</div>
                          <div>✓ Monitoring 300+ job boards...</div>
                          <div>✓ 150+ niche sites active...</div>
                          <div className="text-yellow-400">⚡ New match found: Senior Developer</div>
                          <div className="text-blue-400">📧 Alert sent to phone & email</div>
                          <div className="text-green-400">🎯 You're applicant #3 of 500+</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Button size="lg" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold px-12 py-6 text-xl rounded-full shadow-xl">
              Start Using JobIntel Tools
            </Button>
          </div>
        </div>
      </section>

      {/* Training Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              🎓 Training: JobIntel Academy™
            </h2>
            <p className="text-2xl font-semibold text-gray-800 mb-4">
              JobIntel Academy™ powered by TailorWiz™
            </p>
            <p className="text-xl text-gray-700">
              Master every step of the job search with science-driven training.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {[
              {
                icon: <Target className="w-12 h-12" />,
                title: "The TailorWiz Formula™",
                pain: "Over 96% of résumés never reach a human because they're rejected by ATS bots. Most job seekers guess at tailoring their résumé, keyword-stuffing or copying phrases from the posting — only to still get filtered out.",
                solution: "The TailorWiz Formula™ shows you exactly how to scientifically align your résumé with the job description so it passes ATS 100% of the time and grabs the hiring manager's attention instantly. No guessing. No wasted applications. Just résumés that land interviews.",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: <Calendar className="w-12 h-12" />,
                title: "The 7-Day Job Interview System™",
                pain: "The average job seeker applies for months without hearing back. They get rejection after rejection, or worse — complete silence. Hope fades. Confidence crumbles.",
                solution: "The 7-Day Job Interview System™ gives you a proven step-by-step roadmap to land 3–5 interviews in just 7 days — even if you have no network, no leads, and no current prospects. It takes you out of the endless cycle of waiting and into a predictable rhythm of interviews now.",
                color: "from-green-500 to-emerald-500"
              },
              {
                icon: <Shield className="w-12 h-12" />,
                title: "The ATS Blueprint™",
                pain: "ATS (Applicant Tracking Systems) are the silent killers of job searches. Most job seekers don't even know what the ATS looks for, much less how to beat it. That's why résumés keep getting rejected — even for qualified candidates.",
                solution: "The ATS Blueprint™ is a complete breakdown of the ATS algorithm from A–Z. You'll learn every rule, every filter, and every hidden step — and exactly how to build résumés that get through. Once you understand the ATS, you'll never be eliminated by software again.",
                color: "from-purple-500 to-violet-500"
              },
              {
                icon: <Shield className="w-12 h-12" />,
                title: "Job Scams Exposed 2025™",
                pain: "Nearly 47% of listings on job boards are fake, expired, or scams. Job seekers waste hours applying, give away personal info, and in the worst cases — fall victim to fraud.",
                solution: "Job Scams Exposed 2025™ teaches you how to instantly identify and avoid scam listings. You'll know exactly what red flags to look for, how to verify legitimacy, and how to protect yourself. No more wasted time, no more risk, no more being duped.",
                color: "from-red-500 to-pink-500"
              },
              {
                icon: <Eye className="w-12 h-12" />,
                title: "The Hidden Job Network Playbook™",
                pain: "85% of jobs are never publicly posted on LinkedIn, Indeed, or ZipRecruiter. Yet, 90% of job seekers only compete for the 15% that are. That's why the average online posting gets 500–700 applicants while hidden jobs often get fewer than 20.",
                solution: "The Hidden Job Network Playbook™ shows you how to find and access this secret market. You'll learn where the unlisted jobs are, how to tap into them, and how to compete against 20 people instead of 500+. The result? More interviews. Faster offers. Less competition.",
                color: "from-orange-500 to-red-500"
              },
              {
                icon: <Brain className="w-12 h-12" />,
                title: "The Science of Getting Job Interviews™",
                pain: "The average application success rate online is just 2%. That means out of 100 applications, only 2 result in interviews. For most people, that means months of effort for little to no reward — frustration, burnout, and hopelessness.",
                solution: "The Science of Getting Job Interviews™ is an 8-step masterclass system that consistently delivers 2–5 interviews every week with an interview award rate of over 55% per application. Backed by data, recruiter psychology, and real-world hiring logic, it gives you a precise roadmap to land interviews predictably. No luck. No gimmicks. Just repeatable results.",
                color: "from-indigo-500 to-purple-500"
              }
            ].map((course, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-purple-400 transition-all duration-300 hover:shadow-2xl group h-full">
                <CardHeader>
                  <div className={`w-16 h-16 bg-gradient-to-br ${course.color} rounded-2xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {course.icon}
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                    {course.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <p className="text-sm font-bold text-red-700 mb-2">The Pain:</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{course.pain}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <p className="text-sm font-bold text-green-700 mb-2">The Solution:</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{course.solution}</p>
                  </div>
                  <Button variant="link" className="p-0 h-auto font-bold text-purple-600 hover:text-purple-800">
                    Learn More →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mb-12">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 rounded-3xl shadow-xl max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">With JobIntel Academy™, you won't just learn — you'll master.</h3>
              <p className="text-lg leading-relaxed">
                Each module eliminates the exact obstacles job seekers face — ATS rejection, ghost jobs, scams, hidden job networks, dead-end applications, and failed interviews. Step by step, you'll replace frustration with strategy, confusion with clarity, and rejection with offers.
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <Button size="lg" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold px-12 py-6 text-xl rounded-full shadow-xl">
              Get Access to JobIntel Academy
            </Button>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              ⚖️ Why Job Seekers Fail vs. How JobIntel Suite™ Fixes It
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 p-8">
                <h3 className="text-2xl font-bold text-red-700 mb-6 text-center">The Pain Most Job Seekers Face</h3>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8">
                <h3 className="text-2xl font-bold text-green-700 mb-6 text-center">The JobIntel Solution</h3>
              </div>
            </div>
            
            <div className="divide-y divide-gray-200">
              {[
                {
                  problem: "47% of job postings are fake, expired, or ghost jobs. Applicants waste hours chasing roles that don't exist.",
                  solution: "Job Legitimacy Report™ instantly verifies if a job is real, shows hiring trends (7/30/90 days), and calculates the likelihood they're still hiring. No wasted time."
                },
                {
                  problem: "96% of résumés never reach a human. ATS bots filter them out before managers ever see them.",
                  solution: "The TailorWiz Formula™ + ATS Blueprint™ show you exactly how to craft résumés that pass ATS 100% of the time — and get seen by real decision makers."
                },
                {
                  problem: "500–700+ applicants per posting. Online job boards create impossible competition, with most roles filled before you even apply.",
                  solution: "Advanced Job Search & Tracking Hub™ finds jobs in real time across 300+ boards & 150+ niche sites, plus hidden company pages — so you see opportunities before the crowd."
                },
                {
                  problem: "85% of jobs are never publicly listed. 90% of seekers fight for the 15% that are.",
                  solution: "Hidden Job Network Playbook™ + Career Clarity Match Report™ unlock hidden roles and align you only with jobs that fit your long-term goals."
                },
                {
                  problem: "Most résumés are generic. Hiring managers can't see how you solve their problems.",
                  solution: "Solution Asset Report™ proves you're the exact answer to their top challenges. It's more than a résumé — it's a weapon."
                },
                {
                  problem: "Applications disappear into HR black holes. Job seekers never reach the decision maker.",
                  solution: "Decision Maker's Contact Report™ gives you hiring manager names, titles, and direct outreach roadmaps to bypass HR and go straight to the source."
                },
                {
                  problem: "Most people apply without a system. They lose track, miss steps, and get ghosted.",
                  solution: "JobIntel Workflow™ is the proven 8-step checklist that moves every application from submission to interview with precision."
                },
                {
                  problem: "Even qualified candidates bomb interviews. Nervous answers, vague stories, and lack of prep kill chances.",
                  solution: "JobIntel InterviewMagic™ simulates 1-on-1 or panel interviews tailored to your exact job posting — with AI feedback on answers, tone, and confidence."
                },
                {
                  problem: "Confusion & frustration drag careers down. Without skills or clarity, seekers settle for less or give up.",
                  solution: "JobIntel Academy™ trains you in every major area — ATS, hidden jobs, scams, résumé tailoring, and interview mastery — so you win consistently."
                }
              ].map((row, index) => (
                <div key={index} className="grid md:grid-cols-2">
                  <div className="p-8 bg-red-50/50">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <X className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-gray-800 leading-relaxed">{row.problem}</p>
                    </div>
                  </div>
                  <div className="p-8 bg-green-50/50">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-gray-800 leading-relaxed font-semibold">{row.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              ⭐ What Real Job Seekers Are Saying About JobIntel Suite™
            </h2>
          </div>
          
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">📈 Proven Results Backed by Data</h3>
          </div>
          
          {/* Stats */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[
              {
                icon: <TrendingUp className="w-12 h-12" />,
                stat: "85%",
                description: "Average users report an interview acquisition rate after applying with JobIntel",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: <Award className="w-12 h-12" />,
                stat: "1 in 3",
                description: "Interviews results in a real job offer with desired salary and benefits",
                color: "from-green-500 to-emerald-500"
              },
              {
                icon: <Calendar className="w-12 h-12" />,
                stat: "2-5",
                description: "Most members land interviews per week within their first 14-28 days",
                color: "from-purple-500 to-violet-500"
              },
              {
                icon: <Clock className="w-12 h-12" />,
                stat: "70%",
                description: "Job seekers cut their average search time compared to traditional methods",
                color: "from-orange-500 to-red-500"
              }
            ].map((stat, index) => (
              <Card key={index} className="text-center border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-xl">
                <CardHeader>
                  <div className={`w-20 h-20 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4 text-white shadow-lg`}>
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-black text-gray-900 mb-2">{stat.stat}</div>
                  <p className="text-gray-700 font-medium">{stat.description}</p>
                </CardHeader>
              </Card>
            ))}
          </div>
          
          {/* Success Stories */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">💬 Success Stories</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "From Ghosted to Hired in 3 Weeks",
                  quote: "Before JobIntel, I applied to 120+ jobs in 3 months and heard nothing back. Within 2 weeks of using JobIntel, I had 4 interviews lined up. By week 3, I had an offer — with better pay than I expected. This system flat-out works.",
                  author: "Sarah M.",
                  role: "Marketing Manager"
                },
                {
                  title: "The Hidden Job Market Changed Everything",
                  quote: "I didn't even know 85% of jobs weren't listed online. Once JobIntel showed me how to find them, I wasn't competing with 500 people anymore. I landed 2 interviews in my first week — one of them became my dream role.",
                  author: "David L.",
                  role: "Software Engineer"
                },
                {
                  title: "I Finally Beat the ATS",
                  quote: "I was so tired of rejection emails. JobIntel's TailorWiz Formula helped me create a résumé that actually got through. Within days, I started hearing back. Within weeks, I had multiple offers. I feel like I finally understand the system.",
                  author: "Michelle R.",
                  role: "Project Manager"
                },
                {
                  title: "InterviewMagic Made Me Unstoppable",
                  quote: "I used InterviewMagic to prep for my dream job. It predicted almost every single question they asked in the real interview. I walked in calm, confident, and landed the offer. It felt like cheating (in a good way).",
                  author: "Jason K.",
                  role: "Sales Executive"
                }
              ].map((testimonial, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <Quote className="w-10 h-10 text-blue-500 mb-4" />
                    <CardTitle className="text-xl font-bold text-blue-600 mb-4">{testimonial.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed mb-6 italic text-lg">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {testimonial.author.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{testimonial.author}</p>
                        <p className="text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Trust Factors */}
          <div className="text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">🏆 Why Job Seekers Trust JobIntel</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                "Backed by real-world recruiter logic and tested with thousands of job seekers.",
                "Built on data, psychology, and automation, not guesswork.",
                "Designed for every level — from entry-level to executives.",
                "Infused with a faith-driven mindset: reminding you that rejection is redirection and the right doors will open with preparation."
              ].map((factor, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 hover:border-blue-300 transition-colors">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-4" />
                  <p className="text-gray-700 font-medium">{factor}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-4 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-repeat" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M60 60c16.5 0 30-13.5 30-30S76.5 0 60 0 30 13.5 30 30s13.5 30 30 30zm0 30c16.5 0 30-13.5 30-30S76.5 60 60 60s-30 13.5-30 30 13.5 30 30 30zM30 30c16.5 0 30-13.5 30-30S46.5-30 30-30 0-16.5 0 0s13.5 30 30 30zm60 0c16.5 0 30-13.5 30-30S106.5-30 90-30 60-16.5 60 0s13.5 30 30 30zm0 60c16.5 0 30-13.5 30-30S106.5 30 90 30s-30 13.5-30 30 13.5 30 30 30zm-60 0c16.5 0 30-13.5 30-30S46.5 30 30 30 0 43.5 0 60s13.5 30 30 30z'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="mb-8">
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black hover:from-yellow-300 hover:to-orange-300 text-xl px-8 py-3 font-bold">
              🚀 Final CTA Section
            </Badge>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Stop Job Searching. 
            <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
              Start Job Winning.
            </span>
          </h2>
          
          <p className="text-2xl md:text-3xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
            The JobIntel Suite™ powered by TailorWiz™ gives you everything you need to take control of your career — from job discovery to interview prep to job offers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black hover:from-yellow-300 hover:to-orange-300 font-black px-16 py-8 text-2xl rounded-full shadow-2xl transform hover:scale-105 transition-all duration-200">
              Get JobIntel Suite Today
            </Button>
            <Button size="lg" variant="outline" className="border-3 border-white text-white hover:bg-white hover:text-slate-900 px-16 py-8 text-2xl rounded-full font-bold">
              See Plans & Pricing
            </Button>
          </div>
          
          <div className="mt-16 text-center">
            <p className="text-lg text-blue-200 max-w-3xl mx-auto leading-relaxed">
              Join thousands of job seekers who have transformed their careers with JobIntel Suite™. 
              <span className="text-yellow-300 font-semibold">Your dream job is waiting.</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobIntelSalesPage;